import { lazy, Suspense, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { MainAppLayout } from './app/MainAppLayout';
import { MfaPendingGuard } from './guards/mfa-pending-guard';
import { AuthGuard } from './guards/auth-guard';
import { PublicOnlyGuard } from './guards/public-only-guard';
import { useSession, SESSION_FLAG_KEY } from './features/auth';
import { GlobalLoader } from '@/shared/ui/brand-status';

const LoginPage = lazy(() => import('./features/auth/ui/LoginPage').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./features/auth/ui/RegisterPage').then((m) => ({ default: m.RegisterPage })));
const ForgotPasswordPage = lazy(() => import('./features/auth/ui/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./features/auth/ui/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const MfaEnrollPage = lazy(() => import('./features/auth/ui/MfaEnrollPage').then((m) => ({ default: m.MfaEnrollPage })));
const MfaVerifyPage = lazy(() => import('./features/auth/ui/MfaVerifyPage').then((m) => ({ default: m.MfaVerifyPage })));
const MfaRecoveryPage = lazy(() => import('./features/auth/ui/MfaRecoveryPage').then((m) => ({ default: m.MfaRecoveryPage })));
const OnboardingPage = lazy(() => import('./features/auth/ui/OnboardingPage').then((m) => ({ default: m.OnboardingPage })));

export function App(): JSX.Element {
  const { hydrateFromStorage, silentRefresh, logout, accessToken, loadCurrentUser } = useSession();
  const [isAuthHydrated, setIsAuthHydrated] = useState(false);

  useEffect(() => {
    async function init(): Promise<void> {
      hydrateFromStorage();
      await silentRefresh();
      setIsAuthHydrated(true);
    }
    init();

    function handleStorage(event: StorageEvent): void {
      if (event.key !== SESSION_FLAG_KEY) {
        return;
      }
      if (!event.newValue) {
        logout();
      }
    }

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [hydrateFromStorage, silentRefresh, logout]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }
    void loadCurrentUser();
  }, [accessToken, loadCurrentUser]);

  if (!isAuthHydrated) {
    return <GlobalLoader />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicOnlyGuard>
            <Suspense fallback={<GlobalLoader />}><LoginPage /></Suspense>
          </PublicOnlyGuard>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnlyGuard>
            <Suspense fallback={<GlobalLoader />}><RegisterPage /></Suspense>
          </PublicOnlyGuard>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicOnlyGuard>
            <Suspense fallback={<GlobalLoader />}><ForgotPasswordPage /></Suspense>
          </PublicOnlyGuard>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicOnlyGuard>
            <Suspense fallback={<GlobalLoader />}><ResetPasswordPage /></Suspense>
          </PublicOnlyGuard>
        }
      />
      <Route
        path="/mfa/enroll"
        element={
          <MfaPendingGuard>
            <Suspense fallback={<GlobalLoader />}><MfaEnrollPage /></Suspense>
          </MfaPendingGuard>
        }
      />
      <Route
        path="/mfa/verify"
        element={
          <MfaPendingGuard>
            <Suspense fallback={<GlobalLoader />}><MfaVerifyPage /></Suspense>
          </MfaPendingGuard>
        }
      />
      <Route
        path="/mfa/recovery"
        element={
          <MfaPendingGuard>
            <Suspense fallback={<GlobalLoader />}><MfaRecoveryPage /></Suspense>
          </MfaPendingGuard>
        }
      />
      <Route path="/onboarding" element={<Suspense fallback={<GlobalLoader />}><OnboardingPage /></Suspense>} />
      {/*
        Splatted shell: FeatureRoutes inside MainAppLayout matches RELATIVE paths
        against the remainder so patient-detail nested routes rematch on leave.
      */}
      <Route
        path="/*"
        element={
          <AuthGuard>
            <MainAppLayout />
          </AuthGuard>
        }
      />
    </Routes>
  );
}
